import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import PositionLevelField from '../components/PositionLevelField';
import { createPositionCode, reparentPositionCode } from '../utils/positionCode';
import { useNavigate } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', Accept: 'application/json' } };
};

const PositionList = () => {
    const navigate = useNavigate();
    const companyId = localStorage.getItem('selectedCompanyId') || 1;

    const [positions, setPositions] = useState([]);
    const [departments, setDepartments] = useState([]);
    const wholeCompany = { id: '', code: `CO-${companyId}`, name: 'Whole company' };
    const codeParent = id => departments.find(d => String(d.id) === String(id)) || wholeCompany;
    const [levels, setLevels] = useState([]);
    const [levelsError, setLevelsError] = useState(false);
    const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [showModal, setShowModal] = useState(false);
    const [editingPosition, setEditingPosition] = useState(null);
    const [saving, setSaving] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
    const [showArchiveConfirm, setShowArchiveConfirm] = useState(null);

    // Reporting modal
    const [reportingPosition, setReportingPosition] = useState(null);
    const [reportingData, setReportingData] = useState(null);
    const [reportingLoading, setReportingLoading] = useState(false);
    const [addReportsToId, setAddReportsToId] = useState('');

    const emptyForm = {
        title: '', code: '', description: '', department_id: '', position_level_id: '',
        approved_headcount: 1, status: 'Draft', is_manager_position: false,
    };
    const [formData, setFormData] = useState({ ...emptyForm });

    const handleError = (err) => {
        if (err.response && (err.response.status === 401 || err.response.status === 403)) {
            localStorage.removeItem('token'); localStorage.removeItem('user'); navigate('/login'); return true;
        }
        return false;
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            const [posRes, deptRes] = await Promise.all([
                axios.get(`${API_BASE}/companies/${companyId}/hr-positions`, getAuthHeaders()),
                axios.get(`${API_BASE}/companies/${companyId}/hr-departments`, getAuthHeaders()),
            ]);
            setPositions(posRes.data.data || posRes.data || []);
            setDepartments(deptRes.data.data || deptRes.data || []);

            // Try to fetch position levels
            try {
                const lvlRes = await axios.get(`${API_BASE}/companies/${companyId}/position-levels`, getAuthHeaders());
                setLevels(lvlRes.data.data || lvlRes.data || []);
                setLevelsError(false);
            } catch { setLevels([]); setLevelsError(true); }

            setError(null);
        } catch (err) {
            if (!handleError(err)) setError('Failed to fetch positions.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, [companyId]);

    const openCreate = () => {
        setEditingPosition(null);
        setFormData({ ...emptyForm, code: createPositionCode(wholeCompany.code, positions.map(p => p.code)), department_id: '' });
        setShowModal(true);
    };

    const openEdit = (pos) => {
        setEditingPosition(pos);
        setFormData({
            title: pos.title || '', code: pos.code || '', description: pos.description || '',
            department_id: pos.department_id || '', position_level_id: pos.position_level_id || '',
            approved_headcount: pos.approved_headcount === null ? null : (pos.approved_headcount || 1), status: pos.status || 'Draft',
            is_manager_position: pos.is_manager_position || false,
        });
        setShowModal(true);
    };

    const handleSave = async () => {
        setSaving(true);
        const payload = { ...formData, department_id: formData.department_id || null, position_level_id: formData.position_level_id || null };
        try {
            if (editingPosition) {
                await axios.put(`${API_BASE}/companies/${companyId}/hr-positions/${editingPosition.id}`, payload, getAuthHeaders());
            } else {
                await axios.post(`${API_BASE}/companies/${companyId}/hr-positions`, payload, getAuthHeaders());
            }
            setShowModal(false);
            fetchData();
        } catch (err) {
            if (!handleError(err)) alert(err.response?.data?.message || 'Error saving position.');
        } finally {
            setSaving(false);
        }
    };

    const handleArchive = async (pos) => {
        try {
            await axios.post(`${API_BASE}/companies/${companyId}/hr-positions/${pos.id}/archive`, {}, getAuthHeaders());
            setShowArchiveConfirm(null);
            fetchData();
        } catch (err) {
            if (!handleError(err)) alert(err.response?.data?.message || 'Error archiving position.');
        }
    };

    const handleDelete = async (pos) => {
        try {
            await axios.delete(`${API_BASE}/companies/${companyId}/hr-positions/${pos.id}`, getAuthHeaders());
            setShowDeleteConfirm(null);
            fetchData();
        } catch (err) {
            if (!handleError(err)) alert(err.response?.data?.message || 'Error deleting position.');
        }
    };

    // ── Reporting ──
    const openReportingModal = async (pos) => {
        setReportingPosition(pos);
        setReportingData(null);
        setAddReportsToId('');
        setReportingLoading(true);
        try {
            const res = await axios.get(`${API_BASE}/companies/${companyId}/positions/${pos.id}/reporting`, getAuthHeaders());
            setReportingData(res.data.data || res.data);
        } catch (err) {
            if (!handleError(err)) alert('Error loading reporting data.');
        } finally {
            setReportingLoading(false);
        }
    };

    const refreshReporting = async () => {
        if (!reportingPosition) return;
        try {
            const res = await axios.get(`${API_BASE}/companies/${companyId}/positions/${reportingPosition.id}/reporting`, getAuthHeaders());
            setReportingData(res.data.data || res.data);
        } catch {}
    };

    const handleAddReporting = async () => {
        if (!addReportsToId) return;
        try {
            await axios.post(`${API_BASE}/companies/${companyId}/positions/${reportingPosition.id}/reporting`, { reports_to_position_id: addReportsToId }, getAuthHeaders());
            setAddReportsToId('');
            refreshReporting();
        } catch (err) {
            if (!handleError(err)) alert(err.response?.data?.message || 'Error adding reporting line.');
        }
    };

    const handleRemoveReporting = async (reportsToId) => {
        try {
            await axios.delete(`${API_BASE}/companies/${companyId}/positions/${reportingPosition.id}/reporting/${reportsToId}`, getAuthHeaders());
            refreshReporting();
        } catch (err) {
            if (err.response?.status === 422) {
                const replacementId = window.prompt((err.response.data.message || 'Replacement required.') + '\n\nEnter the replacement position ID:');
                if (replacementId) {
                    try {
                        await axios.delete(`${API_BASE}/companies/${companyId}/positions/${reportingPosition.id}/reporting/${reportsToId}`, { ...getAuthHeaders(), data: { replacement_position_id: replacementId } });
                        refreshReporting();
                    } catch { alert('Error replacing reporting line.'); }
                }
            } else {
                if (!handleError(err)) alert('Error removing reporting line.');
            }
        }
    };

    const updateForm = (field, value) => setFormData(prev => {
        if (field === 'department_id') {
            const oldParent = codeParent(prev.department_id);
            const newParent = codeParent(value);
            return { ...prev, department_id: value, code: reparentPositionCode(prev.code, oldParent?.code, newParent?.code, positions.map(p => p.code)) };
        }
        return { ...prev, [field]: value };
    });

    // Filters
    const filtered = positions.filter(p => {
        if (statusFilter !== 'All' && p.status !== statusFilter) return false;
        if (searchTerm) {
            const s = searchTerm.toLowerCase();
            return (p.title || '').toLowerCase().includes(s) || (p.code || '').toLowerCase().includes(s) || (p.description || '').toLowerCase().includes(s);
        }
        return true;
    });

    // Summary
    const total = positions.length;
    const active = positions.filter(p => p.status === 'Active').length;
    const draft = positions.filter(p => p.status === 'Draft').length;
    const vacant = positions.reduce((sum, p) => sum + (p.vacant_headcount || 0), 0);
    const overHeadcount = positions.filter(p => p.approved_headcount !== null && (p.occupied_headcount || 0) > (p.approved_headcount || 0)).length;

    // ── Styles ──
    const pageStyle = { padding: '24px 32px', backgroundColor: '#f1f5f9', minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' };
    const headerStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' };
    const titleStyle = { fontSize: '1.5rem', fontWeight: '700', color: '#0f172a', margin: 0 };
    const btnPrimary = { padding: '10px 20px', backgroundColor: '#2563eb', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem' };
    const btnDanger = { padding: '6px 14px', backgroundColor: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '0.75rem' };
    const btnWarn = { padding: '6px 14px', backgroundColor: '#fef3c7', color: '#92400e', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '0.75rem' };
    const btnEdit = { padding: '6px 14px', backgroundColor: '#dbeafe', color: '#1d4ed8', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '0.75rem' };
    const btnInfo = { padding: '6px 14px', backgroundColor: '#e0e7ff', color: '#3730a3', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '0.75rem' };

    const cardsRow = { display: 'flex', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' };
    const card = (color) => ({ flex: '1', minWidth: '140px', backgroundColor: '#fff', borderRadius: '12px', padding: '16px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', borderLeft: `4px solid ${color}` });
    const cardLabel = { fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: '600', marginBottom: '4px' };
    const cardValue = { fontSize: '1.5rem', fontWeight: '700', color: '#0f172a' };

    const filterRow = { display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' };
    const pill = (isActive) => ({ padding: '6px 16px', borderRadius: '999px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '0.8rem', backgroundColor: isActive ? '#2563eb' : '#e2e8f0', color: isActive ? '#fff' : '#475569' });
    const searchBox = { padding: '10px 16px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.875rem', width: '280px', outline: 'none' };
    const tableWrap = { backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' };
    const th = { padding: '12px 16px', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0', backgroundColor: '#f8fafc' };
    const td = { padding: '12px 16px', fontSize: '0.875rem', color: '#334155', borderBottom: '1px solid #f1f5f9' };

    const statusBadge = (status) => {
        const map = { Draft: { bg: '#fef3c7', color: '#92400e' }, Active: { bg: '#dcfce7', color: '#166534' }, Inactive: { bg: '#e2e8f0', color: '#475569' }, Archived: { bg: '#f1f5f9', color: '#64748b' } };
        const s = map[status] || map.Draft;
        return { display: 'inline-block', padding: '2px 10px', borderRadius: '999px', fontSize: '0.75rem', fontWeight: '600', backgroundColor: s.bg, color: s.color };
    };
    const managerBadge = { display: 'inline-block', padding: '2px 10px', borderRadius: '999px', fontSize: '0.75rem', fontWeight: '600', backgroundColor: '#f3e8ff', color: '#7c3aed' };

    const headcountBadge = (occ, appr) => {
        const over = occ > appr;
        return { display: 'inline-block', padding: '2px 10px', borderRadius: '999px', fontSize: '0.75rem', fontWeight: '600', backgroundColor: over ? '#fee2e2' : '#dcfce7', color: over ? '#dc2626' : '#166534' };
    };

    const overlay = { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000 };
    const modalBox = { backgroundColor: '#fff', borderRadius: '12px', width: '900px', maxWidth: 'calc(100vw - 32px)', maxHeight: 'calc(100dvh - 32px)', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxSizing: 'border-box', padding: '0' };
    const modalHeader = { flexShrink: 0, padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
    const modalBody = { padding: '20px 24px', minHeight: 0, overflowY: 'auto' };
    const modalFooter = { flexShrink: 0, flexWrap: 'wrap', backgroundColor: '#f8fafc', padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '8px' };
    const inputStyle = { width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box' };
    const selectStyle = { ...inputStyle, backgroundColor: '#fff' };
    const labelStyle = { display: 'block', fontSize: '0.75rem', fontWeight: '600', color: '#374151', marginBottom: '4px' };
    const fieldGroup = { marginBottom: '16px' };
    const gridTwo = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(260px, 100%), 1fr))', gap: '16px' };

    if (initialLoading) return <div style={pageStyle}><p style={{ textAlign: 'center', color: '#64748b', marginTop: '60px' }}>Loading positions...</p></div>;
    if (error) return <div style={pageStyle}><p style={{ textAlign: 'center', color: '#dc2626', marginTop: '60px' }}>{error}</p></div>;

    return (
        <div style={pageStyle}>
            {/* Header */}
            <div style={headerStyle}>
                <h1 style={titleStyle}>Position Structure</h1>
                <button style={btnPrimary} onClick={openCreate}>+ Create Position</button>
            </div>

            {/* Summary Cards */}
            <div style={cardsRow}>
                <div style={card('#2563eb')}><div style={cardLabel}>Total</div><div style={cardValue}>{total}</div></div>
                <div style={card('#16a34a')}><div style={cardLabel}>Active</div><div style={cardValue}>{active}</div></div>
                <div style={card('#d97706')}><div style={cardLabel}>Draft</div><div style={cardValue}>{draft}</div></div>
                <div style={card('#dc2626')}><div style={cardLabel}>Vacant Slots</div><div style={cardValue}>{vacant}</div></div>
                <div style={card('#7c3aed')}><div style={cardLabel}>Over Headcount</div><div style={cardValue}>{overHeadcount}</div></div>
            </div>

            {/* Filters */}
            <div style={filterRow}>
                <button style={pill(statusFilter === 'All')} onClick={() => setStatusFilter('All')}>All</button>
                <button style={pill(statusFilter === 'Active')} onClick={() => setStatusFilter('Active')}>Active</button>
                <button style={pill(statusFilter === 'Draft')} onClick={() => setStatusFilter('Draft')}>Draft</button>
                <button style={pill(statusFilter === 'Inactive')} onClick={() => setStatusFilter('Inactive')}>Inactive</button>
                <button style={pill(statusFilter === 'Archived')} onClick={() => setStatusFilter('Archived')}>Archived</button>
                <div style={{ flex: 1 }} />
                <input style={searchBox} placeholder="Search by title, code..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
            </div>

            {/* Table */}
            <div style={tableWrap}>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <th style={th}>Code</th>
                                <th style={th}>Title</th>
                                <th style={th}>Department</th>
                                <th style={th}>Level</th>
                                <th style={th}>Headcount</th>
                                <th style={th}>Manager</th>
                                <th style={th}>Status</th>
                                <th style={th}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 ? (
                                <tr><td colSpan="8" style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: '40px' }}>No positions found.</td></tr>
                            ) : filtered.map(pos => (
                                <tr key={pos.id} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f8fafc'} onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}>
                                    <td style={{ ...td, fontWeight: '600', color: '#0f172a' }}>{pos.code}</td>
                                    <td style={td}>
                                        <span style={{ fontWeight: '500' }}>{pos.title}</span>
                                        {pos.description && <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '2px' }}>{pos.description}</div>}
                                    </td>
                                    <td style={td}>{pos.department ? pos.department.name : 'Whole company'}</td>
                                    <td style={td}>{pos.level ? pos.level.name || pos.level : (pos.position_level_id || '—')}</td>
                                    <td style={td}>
                                        <span style={headcountBadge(pos.occupied_headcount || 0, pos.approved_headcount === null ? Infinity : pos.approved_headcount)}>
                                            {pos.occupied_headcount || 0} / {pos.approved_headcount === null ? 'No limit' : pos.approved_headcount}
                                        </span>
                                        {(pos.vacant_headcount || 0) > 0 && (
                                            <div style={{ fontSize: '0.7rem', color: '#dc2626', marginTop: '2px' }}>{pos.vacant_headcount} vacant</div>
                                        )}
                                    </td>
                                    <td style={td}>
                                        {pos.is_manager_position ? <span style={managerBadge}>Manager</span> : <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>—</span>}
                                    </td>
                                    <td style={td}><span style={statusBadge(pos.status)}>{pos.status}</span></td>
                                    <td style={td}>
                                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                            <button style={btnEdit} onClick={() => openEdit(pos)}>Edit</button>
                                            <button style={btnInfo} onClick={() => openReportingModal(pos)}>Reporting</button>
                                            {pos.status !== 'Archived' && (
                                                <button style={btnWarn} onClick={() => setShowArchiveConfirm(pos)}>Archive</button>
                                            )}
                                            {pos.status === 'Draft' && (
                                                <button style={btnDanger} onClick={() => setShowDeleteConfirm(pos)}>Delete</button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Create / Edit Modal */}
            {showModal && (
                <div style={overlay} onClick={() => setShowModal(false)}>
                    <div style={modalBox} onClick={e => e.stopPropagation()}>
                        <div style={modalHeader}>
                            <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '700', color: '#0f172a' }}>
                                {editingPosition ? 'Edit Position' : 'Create New Position'}
                            </h2>
                            <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#94a3b8' }}>✕</button>
                        </div>
                        <div style={modalBody}>
                            <div style={gridTwo}>
                                <div style={fieldGroup}>
                                    <label style={labelStyle}>Position Title *</label>
                                    <input style={inputStyle} value={formData.title} onChange={e => updateForm('title', e.target.value)} placeholder="e.g. Software Engineer" />
                                </div>
                                <div style={fieldGroup}>
                                    <label style={labelStyle}>Position Code</label>
                                    <input style={inputStyle} value={formData.code} onChange={e => updateForm('code', e.target.value)} placeholder="Automatically generated on save" maxLength={255} />
                                    <small style={{ display: 'block', marginTop: 6, color: '#64748b', fontSize: '0.75rem', lineHeight: 1.4 }}>Suggested from the department or company code. You can edit the full code.</small>
                                </div>
                            </div>
                            <div style={fieldGroup}>
                                <label style={labelStyle}>Description</label>
                                <textarea style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }} value={formData.description} onChange={e => updateForm('description', e.target.value)} />
                            </div>
                            <div style={gridTwo}>
                                <div style={fieldGroup}>
                                    <label style={labelStyle}>Department / Scope</label>
                                    <select style={selectStyle} value={formData.department_id} onChange={e => updateForm('department_id', e.target.value)}>
                                        <option value="">Whole company</option>
                                        {departments.map(d => <option key={d.id} value={d.id}>{d.name} ({d.code})</option>)}
                                    </select>
                                </div>
                                <div style={fieldGroup}>
                                    <PositionLevelField levels={levels} error={levelsError} value={formData.position_level_id}
                                        onChange={value => updateForm('position_level_id', value)} style={selectStyle} labelStyle={labelStyle} />
                                </div>
                            </div>
                            <div style={gridTwo}>
                                <div style={fieldGroup}>
                                    <label style={labelStyle}>Approved Headcount</label>
                                    <input style={inputStyle} type="number" min="1" disabled={formData.approved_headcount === null} placeholder={formData.approved_headcount === null ? 'No limit' : ''} value={formData.approved_headcount ?? ''} onChange={e => updateForm('approved_headcount', parseInt(e.target.value) || 1)} />
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: '0.8rem' }}><input type="checkbox" checked={formData.approved_headcount === null} onChange={e => updateForm('approved_headcount', e.target.checked ? null : 1)} />No limit</label>
                                </div>
                                <div style={fieldGroup}>
                                    <label style={labelStyle}>Status</label>
                                    <select style={selectStyle} value={formData.status} onChange={e => updateForm('status', e.target.value)}>
                                        <option>Draft</option><option>Active</option><option>Inactive</option><option>Archived</option>
                                    </select>
                                </div>
                            </div>
                            <div style={{ ...fieldGroup, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <input type="checkbox" checked={formData.is_manager_position} onChange={e => updateForm('is_manager_position', e.target.checked)} />
                                <label style={{ fontSize: '0.875rem', color: '#374151', fontWeight: '500' }}>This is a Department Manager position</label>
                            </div>
                        </div>
                        <div style={modalFooter}>
                            <button onClick={() => setShowModal(false)} style={{ ...btnPrimary, backgroundColor: '#e2e8f0', color: '#475569' }}>Cancel</button>
                            <button onClick={handleSave} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>
                                {saving ? 'Saving...' : (editingPosition ? 'Update Position' : 'Create Position')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Reporting Modal */}
            {reportingPosition && (
                <div style={overlay} onClick={() => setReportingPosition(null)}>
                    <div style={{ ...modalBox, width: '520px' }} onClick={e => e.stopPropagation()}>
                        <div style={modalHeader}>
                            <div>
                                <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: '#0f172a' }}>Reporting Lines</h2>
                                <p style={{ margin: '4px 0 0 0', fontSize: '0.8rem', color: '#64748b' }}>{reportingPosition.title} ({reportingPosition.code})</p>
                            </div>
                            <button onClick={() => setReportingPosition(null)} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#94a3b8' }}>✕</button>
                        </div>
                        <div style={modalBody}>
                            {reportingLoading ? (
                                <p style={{ textAlign: 'center', color: '#94a3b8' }}>Loading...</p>
                            ) : reportingData && (
                                <>
                                    <div style={{ marginBottom: '20px' }}>
                                        <h4 style={{ fontSize: '0.75rem', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', marginBottom: '8px' }}>Reports To</h4>
                                        {(reportingData.reports_to || []).length === 0 ? (
                                            <p style={{ fontSize: '0.85rem', color: '#94a3b8' }}>No reporting lines set.</p>
                                        ) : (reportingData.reports_to || []).map(upper => (
                                            <div key={upper.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid #f1f5f9' }}>
                                                <span style={{ fontSize: '0.875rem', color: '#334155' }}>
                                                    {upper.title} ({upper.code})
                                                    {upper.is_vacant && <span style={{ marginLeft: '6px', fontSize: '0.7rem', padding: '1px 6px', borderRadius: '999px', backgroundColor: '#fee2e2', color: '#dc2626' }}>Vacant</span>}
                                                </span>
                                                <button onClick={() => handleRemoveReporting(upper.id)} style={{ ...btnDanger, fontSize: '0.7rem', padding: '3px 10px' }}>Remove</button>
                                            </div>
                                        ))}
                                    </div>

                                    <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
                                        <select value={addReportsToId} onChange={e => setAddReportsToId(e.target.value)} style={{ ...selectStyle, flex: 1 }}>
                                            <option value="">Select position to report to...</option>
                                            {positions.filter(p => p.id !== reportingPosition.id).map(p => (
                                                <option key={p.id} value={p.id}>{p.title} ({p.code})</option>
                                            ))}
                                        </select>
                                        <button onClick={handleAddReporting} style={btnPrimary}>Add</button>
                                    </div>

                                    <div>
                                        <h4 style={{ fontSize: '0.75rem', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', marginBottom: '8px' }}>Direct Reports</h4>
                                        {(reportingData.direct_reports || []).length === 0 ? (
                                            <p style={{ fontSize: '0.85rem', color: '#94a3b8' }}>No positions report to this one.</p>
                                        ) : (reportingData.direct_reports || []).map(lower => (
                                            <div key={lower.id} style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9', fontSize: '0.875rem', color: '#334155' }}>
                                                {lower.title} ({lower.code})
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                        <div style={modalFooter}>
                            <button onClick={() => setReportingPosition(null)} style={{ ...btnPrimary, backgroundColor: '#e2e8f0', color: '#475569' }}>Close</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Archive Confirm */}
            {showArchiveConfirm && (
                <div style={overlay} onClick={() => setShowArchiveConfirm(null)}>
                    <div style={{ ...modalBox, width: '420px' }} onClick={e => e.stopPropagation()}>
                        <div style={modalHeader}><h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: '#0f172a' }}>Archive Position</h2></div>
                        <div style={modalBody}><p style={{ color: '#475569', margin: 0 }}>Archive <strong>"{showArchiveConfirm.title}"</strong>? New employee assignments will be blocked.</p></div>
                        <div style={modalFooter}>
                            <button onClick={() => setShowArchiveConfirm(null)} style={{ ...btnPrimary, backgroundColor: '#e2e8f0', color: '#475569' }}>Cancel</button>
                            <button onClick={() => handleArchive(showArchiveConfirm)} style={{ ...btnPrimary, backgroundColor: '#d97706' }}>Archive</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirm */}
            {showDeleteConfirm && (
                <div style={overlay} onClick={() => setShowDeleteConfirm(null)}>
                    <div style={{ ...modalBox, width: '420px' }} onClick={e => e.stopPropagation()}>
                        <div style={modalHeader}><h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: '#dc2626' }}>Delete Position</h2></div>
                        <div style={modalBody}><p style={{ color: '#475569', margin: 0 }}>Permanently delete <strong>"{showDeleteConfirm.title}"</strong>? Only Draft positions with no employees can be deleted.</p></div>
                        <div style={modalFooter}>
                            <button onClick={() => setShowDeleteConfirm(null)} style={{ ...btnPrimary, backgroundColor: '#e2e8f0', color: '#475569' }}>Cancel</button>
                            <button onClick={() => handleDelete(showDeleteConfirm)} style={{ ...btnPrimary, backgroundColor: '#dc2626' }}>Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PositionList;
